import type { BaseComponentProps } from '@json-render/react';
import { useBoundProp } from '@json-render/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isFinite from 'lodash/isFinite';
import isString from 'lodash/isString';
import join from 'lodash/join';
import map from 'lodash/map';
import startsWith from 'lodash/startsWith';
import uniq from 'lodash/uniq';
import { ExternalLink } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { z } from 'zod';

import type { SatelliteImageProps as SatelliteImageSchema } from './catalog';

type SatelliteImageProps = z.infer<typeof SatelliteImageSchema>;
type Marker = NonNullable<SatelliteImageProps['markers']>[number];

/** GET /api/satellite/scene response. */
type SatelliteScene = {
  scene_id: string;
  image_url: string;
  as_of: string;
  mode: string;
  bbox: number[];
  cloud_cover_pct: number | null;
  resolution_m: number | null;
  copernicus_url: string;
  firms_url: string;
};

const MODE_LABELS: Record<string, string> = {
  true_color: 'True color',
  swir_fire: 'SWIR',
  false_color_veg: 'False color',
  ndvi: 'NDVI',
};

// image_url is presigned for 1 h; refetch before it lapses.
const SCENE_STALE_MS = 50 * 60 * 1000;

/** A src the browser can load — not empty and not a chatbot-data:// placeholder still streaming. */
export const isLoadableImageSrc = (src: unknown): src is string =>
  isString(src) && src !== '' && !startsWith(src, 'chatbot-data://');

const isExternalHref = (href: unknown): href is string => isString(href) && startsWith(href, 'https://');

/**
 * Image that opens its source viewer (Copernicus Browser, NASA FIRMS) in a new
 * tab on click. Shared by SatelliteImage and DataTable image cells. Without an
 * https `href` it renders as a plain image.
 */
export const LinkedImage = ({
  src,
  alt,
  href,
  className,
  overlay,
}: {
  src: string;
  alt: string;
  href?: unknown;
  className?: string;
  overlay?: React.ReactNode;
}) => {
  const body = (
    <>
      <img src={src} alt={alt} loading="lazy" className="block h-full w-full object-cover" />
      {overlay}
    </>
  );
  const frame = `group relative block overflow-hidden rounded-md bg-muted ${className ?? ''}`;
  if (!isExternalHref(href)) return <div className={frame}>{body}</div>;
  return (
    // noopener: the viewer is a third-party site and must not get window.opener.
    <a href={href} target="_blank" rel="noopener noreferrer" title={`Open ${alt} in source viewer`} className={frame}>
      {body}
      <span className="absolute right-1.5 top-1.5 rounded bg-black/60 p-1 text-white opacity-70 group-hover:opacity-100">
        <ExternalLink className="h-3 w-3" />
      </span>
    </a>
  );
};

/**
 * Pins in percentage coordinates so the overlay scales with the image. Linear
 * lat/lon → pixel mapping inside `bbox`; scenes are a few km across, where the
 * projection error is sub-pixel.
 */
const MarkerOverlay = ({ markers, bbox }: { markers: Marker[]; bbox: number[] }) => {
  const [west, south, east, north] = bbox;
  return (
    <div className="pointer-events-none absolute inset-0">
      {map(markers, (m, i) => {
        const left = ((m.lon - west) / (east - west)) * 100;
        const top = ((north - m.lat) / (north - south)) * 100;
        if (!isFinite(left) || !isFinite(top) || left < 0 || left > 100 || top < 0 || top > 100) return null;
        return (
          <div key={i} className="absolute" style={{ left: `${left}%`, top: `${top}%` }}>
            <div
              className="-translate-x-1/2 -translate-y-1/2 h-3 w-3 rounded-full border-2 border-white"
              style={{ backgroundColor: m.color ?? '#ef4444', boxShadow: '0 0 0 1px rgba(0,0,0,0.4)' }}
            />
            {m.label && (
              <div className="absolute left-2 -top-2 whitespace-nowrap rounded bg-black/70 px-1 py-px text-[10px] text-white">
                {m.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DEFAULT_MODE = 'true_color';

/** Query options for one scene render — shared by the visible query and the tab prefetch. */
const sceneQuery = (props: SatelliteImageProps, mode: string) => {
  const { sceneId, lat, lon } = props;
  const radiusKm = props.radiusKm ?? 2;
  return {
    queryKey: ['genui-satellite-scene', sceneId, lat, lon, radiusKm, mode],
    queryFn: async (): Promise<SatelliteScene> => {
      const client = await OilyticsClient.create();
      const resp = await client.getSatelliteScene({ scene_id: sceneId, lat, lon, radius_km: radiusKm, mode });
      return resp?.data;
    },
    staleTime: SCENE_STALE_MS,
  };
};

const isQueryable = (props: SatelliteImageProps) => isString(props.sceneId) && isFinite(props.lat) && isFinite(props.lon);

/**
 * The active render mode. Bound (`mode: { $bindState: '/path' }`) → the shared
 * state value, so every image bound to the path switches together. Unbound →
 * local state seeded from the `mode` prop; useBoundProp's setter is a no-op
 * without a binding path, hence the fallback.
 */
const useActiveMode = (propMode: string | undefined, bindingPath: string | undefined) => {
  const [boundMode, setBoundMode] = useBoundProp<string | undefined>(propMode, bindingPath);
  const [localMode, setLocalMode] = useState(propMode ?? DEFAULT_MODE);
  // Streaming patches can update the prop after mount.
  useEffect(() => {
    if (propMode) setLocalMode(propMode);
  }, [propMode]);
  if (bindingPath) return [boundMode ?? DEFAULT_MODE, setBoundMode] as const;
  return [localMode, setLocalMode] as const;
};

export const SatelliteImage = ({ props, bindings }: BaseComponentProps<SatelliteImageProps>) => {
  const [mode, setMode] = useActiveMode(props.mode, bindings?.mode);
  const tabModes = filter(isArray(props.modes) ? uniq(props.modes) : [], (m) => has(MODE_LABELS, m));
  const queryClient = useQueryClient();
  const { data: scene, error, isLoading } = useQuery({
    ...sceneQuery(props, mode),
    enabled: isQueryable(props),
    // 400/404 (bad id, scene doesn't cover the point) won't change on retry.
    retry: (failureCount, err) => get(err, 'response.status', 500) >= 500 && failureCount < 2,
  });
  const alt = props.title ?? 'Satellite image';

  // Once the visible render lands, warm the other tabs so switching is instant
  // (each is a few-second render on a cache miss). prefetchQuery skips modes
  // already cached and fresh.
  const sceneLoaded = Boolean(scene);
  const tabKey = join(tabModes, ',');
  useEffect(() => {
    if (!sceneLoaded) return;
    forEach(tabModes, (m) => {
      if (m !== mode) queryClient.prefetchQuery(sceneQuery(props, m));
    });
    // props identity changes every render; the scene identity fields are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneLoaded, tabKey, props.sceneId, props.lat, props.lon, props.radiusKm]);

  let body: React.ReactNode;
  if (scene) {
    const overlay = isArray(props.markers) && props.markers.length > 0 && isArray(scene.bbox)
      ? <MarkerOverlay markers={props.markers} bbox={scene.bbox} />
      : undefined;
    const href = props.link === 'firms' ? scene.firms_url : scene.copernicus_url;
    body = <LinkedImage src={scene.image_url} alt={alt} href={href} className="aspect-square w-full" overlay={overlay} />;
  } else if (error) {
    body = (
      <div className="flex aspect-square w-full items-center justify-center rounded-md bg-muted p-4 text-center text-xs text-muted-foreground">
        {get(error, 'response.data.message', 'Satellite image unavailable')}
      </div>
    );
  } else {
    // Covers streaming (props still arriving) and the render itself, which
    // takes a few seconds on a cache miss.
    body = (
      <div className={`aspect-square w-full rounded-md bg-muted ${isLoading ? 'animate-pulse' : ''}`} />
    );
  }

  const caption = scene
    ? join(
      compact([
        'Copernicus Sentinel-2',
        MODE_LABELS[scene.mode] ?? scene.mode,
        scene.as_of,
        isFinite(scene.cloud_cover_pct) ? `${scene.cloud_cover_pct}% cloud` : null,
      ]),
      ' · ',
    )
    : null;

  return (
    <div className="flex flex-col gap-1">
      {props.title && <div className="text-sm font-semibold text-foreground">{props.title}</div>}
      {tabModes.length > 1 && (
        <ToggleGroup
          type="single"
          value={mode}
          // Radix sends '' when the active item is clicked again — keep the current mode.
          onValueChange={(v) => v && setMode(v)}
          className="justify-start"
        >
          {map(tabModes, (m) => (
            <ToggleGroupItem key={m} value={m} size="sm" className="h-6 text-xs">
              {MODE_LABELS[m]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
      {body}
      {caption && <div className="text-xs text-muted-foreground">{caption}</div>}
    </div>
  );
};
